import { ReactNode } from 'react';

// mui
import { Badge, BadgeProps, Box, SxProps, Tooltip, TooltipProps } from '@mui/material';

// ui
import { Cube } from '../Cube/Cube';

// theme
import { theme } from '../../../theme';

// icons
import {
	AccountBalanceOutlined as IconAccountBalance,
	AddchartOutlined as IconAddchart,
	AlignHorizontalLeftOutlined as IconAlignHorizontalLeft,
	ArrowBackOutlined as IconArrowBack,
	ArrowDownwardOutlined as IconArrowDownward,
	ArrowForwardOutlined as IconArrowForward,
	ArrowUpwardOutlined as IconArrowUpward,
	AssessmentOutlined as IconAssessment,
	AssignmentOutlined as IconAssignment,
	AssignmentReturnOutlined as IconAssignmentReturn,
	BalanceOutlined as IconBalance,
	CalculateOutlined as IconCalculate,
	CalendarTodayOutlined as IconCalendarToday,
	CategoryOutlined as IconCategory,
	CheckOutlined as IconCheck,
	CloseOutlined as IconClose,
	CloudUploadOutlined as IconCloudUpload,
	CommentOutlined as IconComment,
	CorporateFareOutlined as IconCorporateFare,
	CreateOutlined as IconCreate,
	DescriptionOutlined as IconDescription,
	DoubleArrowOutlined as IconDoubleArrow,
	ExpandMoreOutlined as IconExpandMore,
	ExtensionOutlined as IconExtension,
	FlagOutlined as IconFlag,
	FolderOutlined as IconFolder,
	GradeOutlined as IconGrade,
	InfoOutlined as IconInfo,
	InsertDriveFileOutlined as IconInsertDriveFile,
	Inventory2Outlined as IconInventory2,
	LocalAtmOutlined as IconLocalAtm,
	LocalOfferOutlined as IconLocalOffer,
	MoveToInboxOutlined as IconMoveToInbox,
	NotificationsOutlined as IconNotifications,
	PageviewOutlined as IconPageview,
	PaidOutlined as IconPaid,
	PaymentsOutlined as IconPayments,
	PeopleOutlineOutlined as IconPeople,
	PersonOutlined as IconPerson,
	PlaceOutlined as IconPlace,
	PriorityHighOutlined as IconPriorityHigh,
	PublicOutlined as IconPublic,
	QuestionMarkOutlined as IconQuestionMark,
	ReceiptOutlined as IconReceipt,
	RemoveOutlined as IconRemove,
	ReplayOutlined as IconReplenishment,
	RequestQuoteOutlined as IconRequestQuote,
	SearchOutlined as IconSearch,
	StoreOutlined as IconStore,
	SummarizeOutlined as IconSummarize,
	SwapHorizOutlined as IconTransfer,
	SyncOutlined as IconSync,
	ViewListOutlined as IconList,
	StorefrontOutlined as IconStorefront,
	VolunteerActivismOutlined as IconVolunteerActivism,
	WarningAmberOutlined as IconWarning,
} from '@mui/icons-material';

// img
import { LogoWorldBankMark } from '../../img/LogoWorldBankMark';

// Re-export type mapping utilities for convenience
export {
	entityIconSubtypes,
	entityIconTypes,
	financierIconTypes,
	ilapCategoryIconTypes,
	transactionIconTypes,
} from './IconCircle.utils';

// props
export const iconCircleColors = [
	'blue',
	'error',
	'green',
	'grey',
	'info',
	'magenta',
	'orange',
	'purple',
	'success',
	'teal',
	'warning',
	'yellow',
] as const;
export const iconCircleSizes = ['xs', 'sm', 'md', 'lg', 'xl'] as const;
export const iconCircleTypes = [
	'account',
	'act',
	'advance',
	'arrowBackward',
	'arrowDownward',
	'arrowForward',
	'arrowUpward',
	'auditor',
	'benefit',
	'beneficiary',
	'borrower',
	'cashBenefit',
	'cashInMethod',
	'category',
	'check',
	'close',
	'comment',
	'component',
	'contract',
	'cube',
	'date',
	'default',
	'description',
	'direct',
	'document',
	'documentType',
	'donor',
	'edit',
	'entity',
	'expandMore',
	'expenseType',
	'form',
	'fundFlow',
	'government',
	'help',
	'ifr',
	'ilap',
	'individual',
	'invoice',
	'loan',
	'local',
	'location',
	'minus',
	'nonCashBenefit',
	'notification',
	'opex',
	'organization',
	'other',
	'payment',
	'plan',
	'priority',
	'project',
	'reconciliationItem',
	'reimbursement',
	'replenishment',
	'search',
	'specialCommitment',
	'subPiu',
	'supplier',
	'tag',
	'tax',
	'transfer',
	'upload',
	'user',
	'vendor',
	'warning',
] as const;

export type IconCircleColor = (typeof iconCircleColors)[number];
export type IconCircleSize = (typeof iconCircleSizes)[number];
export type IconCircleType = (typeof iconCircleTypes)[number];

export interface IconCircleVariant {
	color: IconCircleColor;
	icon: ReactNode;
}

export type IconCircleBadge = BadgeProps & { TooltipProps?: Omit<TooltipProps, 'children'> };

export interface IconCircleProps {
	badges?: IconCircleBadge[];
	color?: IconCircleColor;
	icon?: ReactNode;
	scheme?: 'dark' | 'light';
	size?: IconCircleSize;
	sxBox?: SxProps;
	TooltipProps?: Omit<TooltipProps, 'children'>;
	type?: IconCircleType;
	variant?: 'filled' | 'outlined';
}

export const IconCircle = ({
	badges,
	color,
	icon,
	scheme = 'light',
	size = 'md',
	sxBox,
	TooltipProps,
	type = 'default',
	variant = 'filled',
}: IconCircleProps) => {
	// vars
	const sizesIconCustom: Record<string, number> = {
		xs: 16,
		sm: 20,
		md: 24,
		lg: 28,
		xl: 32,
	};

	const sizesCube: Record<string, number> = {
		xs: 8,
		sm: 12,
		md: 16,
		lg: 22,
		xl: 24,
	};

	const sizes = {
		xs: {
			width: 24,
			height: 24,
		},
		sm: {
			width: 32,
			height: 32,
		},
		md: {
			width: 42,
			height: 42,
		},
		lg: {
			width: 64,
			height: 64,
		},
		xl: {
			width: 80,
			height: 80,
		},
	};

	const colors = {
		blue: {
			light: theme.palette.brand.blue[200],
			dark: theme.palette.brand.blue[700],
		},
		default: {
			light: theme.palette.brand.grey[200],
			dark: theme.palette.brand.grey[700],
		},
		error: {
			light: theme.palette.error.light,
			dark: theme.palette.error.dark,
		},
		green: {
			light: theme.palette.brand.green[100],
			dark: theme.palette.brand.green[700],
		},
		grey: {
			light: theme.palette.brand.grey[200],
			dark: theme.palette.brand.grey[700],
		},
		help: {
			light: theme.palette.brand.grey[200],
			dark: theme.palette.brand.grey[700],
		},
		info: {
			light: theme.palette.info.light,
			dark: theme.palette.info.dark,
		},
		magenta: {
			light: theme.palette.brand.magenta[100],
			dark: theme.palette.brand.magenta[700],
		},
		orange: {
			light: theme.palette.brand.orange[100],
			dark: theme.palette.brand.orange[700],
		},
		purple: {
			light: theme.palette.brand.purple[100],
			dark: theme.palette.brand.purple[700],
		},
		success: {
			light: theme.palette.success.light,
			dark: theme.palette.success.dark,
		},
		teal: {
			light: theme.palette.brand.teal[100],
			dark: theme.palette.brand.teal[700],
		},
		warning: {
			light: theme.palette.warning.light,
			dark: theme.palette.warning.dark,
		},
		yellow: {
			light: theme.palette.brand.yellow[100],
			dark: theme.palette.brand.yellow[700],
		},
	};

	const types: Record<string, IconCircleVariant> = {
		account: {
			color: 'blue',
			icon: <LogoWorldBankMark style={{ width: sizesIconCustom[size] }} />,
		},
		act: {
			color: 'purple',
			icon: <IconInventory2 />,
		},
		advance: {
			color: 'blue',
			icon: <IconDoubleArrow />,
		},
		arrowBackward: {
			color: 'grey',
			icon: <IconArrowBack />,
		},
		arrowDownward: {
			color: 'grey',
			icon: <IconArrowDownward />,
		},
		arrowForward: {
			color: 'grey',
			icon: <IconArrowForward />,
		},
		arrowUpward: {
			color: 'grey',
			icon: <IconArrowUpward />,
		},
		auditor: {
			color: 'magenta',
			icon: <IconPageview />,
		},
		benefit: {
			color: 'blue',
			icon: <IconVolunteerActivism />,
		},
		beneficiary: {
			color: 'magenta',
			icon: <IconPerson />,
		},
		borrower: {
			color: 'magenta',
			icon: <IconAccountBalance />,
		},
		cashBenefit: {
			color: 'blue',
			icon: <IconPaid />,
		},
		cashInMethod: {
			color: 'blue',
			icon: <IconMoveToInbox />,
		},
		category: {
			color: 'blue',
			icon: <IconCategory />,
		},
		check: {
			color: 'green',
			icon: <IconCheck />,
		},
		close: {
			color: 'grey',
			icon: <IconClose />,
		},
		comment: {
			color: 'blue',
			icon: <IconComment />,
		},
		component: {
			color: 'blue',
			icon: <IconExtension />,
		},
		contract: {
			color: 'yellow',
			icon: <IconAssignment />,
		},
		cube: {
			color: 'grey',
			icon: <Cube size={sizesCube[size]} />,
		},
		date: {
			color: 'grey',
			icon: <IconCalendarToday />,
		},
		nonCashBenefit: {
			color: 'blue',
			icon: <IconVolunteerActivism />,
		},
		default: {
			color: 'grey',
			icon: <IconInfo />,
		},
		description: {
			color: 'blue',
			icon: <IconDescription />,
		},
		direct: {
			color: 'warning',
			icon: <LogoWorldBankMark />,
		},
		document: {
			color: 'green',
			icon: <IconInsertDriveFile />,
		},
		documentType: {
			color: 'green',
			icon: <IconInsertDriveFile />,
		},
		donor: {
			color: 'magenta',
			icon: <IconAddchart />,
		},
		edit: {
			color: 'grey',
			icon: <IconCreate />,
		},
		entity: {
			color: 'magenta',
			icon: <IconAccountBalance />,
		},
		expandMore: {
			color: 'grey',
			icon: <IconExpandMore />,
		},
		expenseType: {
			color: 'blue',
			icon: <IconRequestQuote />,
		},
		form: {
			color: 'magenta',
			icon: <IconList />,
		},
		fundFlow: {
			color: 'blue',
			icon: <IconAlignHorizontalLeft />,
		},
		government: {
			color: 'grey',
			icon: <IconAccountBalance />,
		},
		help: {
			color: 'grey',
			icon: <IconQuestionMark />,
		},
		ifr: {
			color: 'purple',
			icon: <IconAssessment />,
		},
		ilap: {
			color: 'orange',
			icon: <IconSync />,
		},
		individual: {
			color: 'magenta',
			icon: <IconPerson />,
		},
		invoice: {
			color: 'yellow',
			icon: <IconReceipt />,
		},
		loan: {
			color: 'teal',
			icon: <IconPayments />,
		},
		local: {
			color: 'blue',
			icon: <IconFlag />,
		},
		location: {
			color: 'grey',
			icon: <IconPlace />,
		},
		minus: {
			color: 'grey',
			icon: <IconRemove />,
		},
		notification: {
			color: 'grey',
			icon: <IconNotifications />,
		},
		opex: {
			color: 'blue',
			icon: <IconLocalAtm />,
		},
		organization: {
			color: 'magenta',
			icon: <IconPeople />,
		},
		other: {
			color: 'grey',
			icon: <IconPublic />,
		},
		payment: {
			color: 'blue',
			icon: <IconPaid />,
		},
		plan: {
			color: 'purple',
			icon: <IconSummarize />,
		},
		priority: {
			color: 'warning',
			icon: <IconPriorityHigh />,
		},
		project: {
			color: 'purple',
			icon: <IconFolder />,
		},
		reconciliationItem: {
			color: 'blue',
			icon: <IconBalance />,
		},
		reimbursement: {
			color: 'blue',
			icon: <IconAssignmentReturn />,
		},
		replenishment: {
			color: 'blue',
			icon: <IconReplenishment />,
		},
		search: {
			color: 'grey',
			icon: <IconSearch />,
		},
		specialCommitment: {
			color: 'blue',
			icon: <IconGrade />,
		},
		subPiu: {
			color: 'magenta',
			icon: <IconCorporateFare />,
		},
		supplier: {
			color: 'magenta',
			icon: <IconStore />,
		},
		tag: {
			color: 'blue',
			icon: <IconLocalOffer />,
		},
		tax: {
			color: 'grey',
			icon: <IconCalculate />,
		},
		transfer: {
			color: 'blue',
			icon: <IconTransfer />,
		},
		upload: {
			color: 'grey',
			icon: <IconCloudUpload />,
		},
		user: {
			color: 'orange',
			icon: <IconPerson />,
		},
		vendor: {
			color: 'yellow',
			icon: <IconStorefront />,
		},
		warning: {
			color: 'warning',
			icon: <IconWarning />,
		},
	};

	let content = (
		<Box
			sx={(theme) => {
				let sx: SxProps = {
					backgroundColor: theme.palette.brand.grey[200],
					borderRadius: '50%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					color: theme.palette.brand.grey[700],
					'& svg': {
						width: '60%',
						maxHeight: '56%',
					},
					...sizes[size],
				} as object;

				let colorDefined: IconCircleColor = 'grey';

				if (type && types[type]) {
					colorDefined = types[type].color;
				}

				if (color) {
					colorDefined = color;
				}

				if (scheme === 'light') {
					sx = {
						...sx,
						backgroundColor: colors[colorDefined].light,
						color: colors[colorDefined].dark,
					};
				} else {
					sx = {
						...sx,
						backgroundColor: colors[colorDefined].dark,
						color: colors[colorDefined].light,
					};
				}

				if (variant === 'outlined') {
					let colorOutlined = colors[colorDefined].light;

					if (scheme === 'dark') {
						colorOutlined = colors[colorDefined].dark;
					}

					sx = {
						...sx,
						backgroundColor: 'transparent',
						borderStyle: 'solid',
						borderWidth: 2,
						borderColor: colorOutlined,
						color: colorOutlined,
					};
				}

				return {
					...sx,
					...(sxBox as object),
				};
			}}>
			{icon || (type && types[type] ? types[type].icon : types.default.icon)}
		</Box>
	);

	if (badges && badges.length > 0) {
		for (const badge of badges) {
			const { TooltipProps: badgeTooltipProps, badgeContent, ...badgeRest } = badge;

			let contentBadge = badgeContent;

			if (badgeTooltipProps) {
				contentBadge = (
					<Tooltip arrow={true} placement="top" {...badgeTooltipProps}>
						<div>{badgeContent}</div>
					</Tooltip>
				);
			}

			content = (
				<Badge badgeContent={contentBadge} {...badgeRest}>
					{content}
				</Badge>
			);
		}
	}

	if (TooltipProps) {
		content = (
			<Tooltip arrow={true} placement="top" {...TooltipProps}>
				{content}
			</Tooltip>
		);
	}

	return content;
};
